import * as generalLedgerAccountHooks from "@rentiohq/shared-frontend/dist/reduxV2/generalLedgerAccount/generalLedgerAccount.hooks";
import { IGeneralLedgerAccount } from "@rentiohq/shared-frontend/dist/reduxV2/generalLedgerAccount/generalLedgerAccount.types";
import { IPagedQuery } from "@rentiohq/shared-frontend/dist/reduxV2/utils/api.types";
import { EPaymentOrderType } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { PAYMENT_ORDER_SORTING } from "@rentiohq/shared-frontend/dist/utils/paymentOrder.utils";
import {
  Card,
  DataTable,
  EmptyState,
  Error,
  IDataTableItem,
  Loading,
  mapItemsToDataTableProps,
} from "@rentiohq/web-shared/dist/components";
import { compact, indexOf, sortBy } from "lodash";
import { getContactListPagedParams } from "../contactOrMeMaster.utils";
import { useContactOrMeMaster } from "../useContactOrMeMaster";

interface IProps {
  asCard?: boolean;
}

const GENERAL_LEDGER_ACCOUNTS_QUERY: IPagedQuery = {
  page: 1,
  limit: 100,
  sort: [
    { field: "paymentOrderType", method: "ASC" },
    { field: "typePayment", method: "ASC" },
  ],
};

const visuallyOrderGeneralLedgerAccounts = (input: IGeneralLedgerAccount[]) => {
  return sortBy(input, [
    x => indexOf(PAYMENT_ORDER_SORTING, x.paymentOrderType),
    x => x.typePayment,
    x => x.code,
    x => x.description,
  ]);
};

export const ContactGeneralLedgerAccounts = (props: IProps) => {
  const { asCard = true } = props;

  const contact = useContactOrMeMaster();

  const {
    items: _items,
    isFetching: isFetchingItems,
    fetchError: itemsFetchError,
  } = generalLedgerAccountHooks.usePaged(
    getContactListPagedParams({
      contactId: contact.contactId,
      segment: "general-ledger-accounts",
      query: GENERAL_LEDGER_ACCOUNTS_QUERY,
    }),
  );
  const items = _items ? visuallyOrderGeneralLedgerAccounts(_items) : undefined;
  const isFetching = isFetchingItems || contact.isFetching;
  const fetchError = itemsFetchError ?? contact.fetchError;

  const dataTableItems: IDataTableItem<IGeneralLedgerAccount>[] = [
    {
      heading: getLocalizedText("payment_request.follow_up.payment.heading"),
      columnContentType: "text",
      sortable: false,
      order: undefined,
      renderContent: (item: IGeneralLedgerAccount) => {
        const paymentOrderType = getLocalizedText(
          `payment.payment_order.type.${item.paymentOrderType}`.toLowerCase(),
        );
        const paymentType = item.typePayment
          ? getLocalizedText(
              `payment.payment_order.payment_type.value.${item.typePayment}`.toLowerCase(),
            )
          : undefined;

        let recurrenceType: string | undefined;
        if (item.paymentOrderType === EPaymentOrderType.OtherRecurring) {
          recurrenceType = getLocalizedText(
            "payments.create.add_costs.monthly_costs",
          );
        } else if (item.paymentOrderType === EPaymentOrderType.OtherOneoff) {
          recurrenceType = getLocalizedText(
            "payments.create.add_costs.oneoff_costs",
          );
        }

        return compact([paymentOrderType, paymentType, recurrenceType]).join(
          " - ",
        );
      },
    },
    {
      heading: getLocalizedText("system.entity.general_ledger_account"),
      columnContentType: "text",
      sortable: false,
      order: undefined,
      renderContent: (item: IGeneralLedgerAccount) => item.code,
    },
    {
      heading: getLocalizedText("system.description"),
      columnContentType: "text",
      sortable: false,
      order: undefined,
      renderContent: (item: IGeneralLedgerAccount) => item.description,
    },
  ];
  const dataTableItemsMapped = mapItemsToDataTableProps(dataTableItems);

  const content = (
    <>
      {isFetching && !items && <Loading />}

      {!isFetching && !items && fetchError && <Error errors={[fetchError]} />}

      {items && (
        <>
          {/* <Banner
            variation="info"
            title={getLocalizedText("general_ledger_account.info.title")}
            hasDismiss={false}
          >
            {getLocalizedText("general_ledger_account.info.content")}
          </Banner> */}

          <DataTable
            rows={items.map(item => ({
              id: item.id,
              content: dataTableItems.map(dataTableItem =>
                dataTableItem.renderContent(item),
              ),
            }))}
            {...dataTableItemsMapped}
            emptyState={
              <EmptyState
                heading={getLocalizedText(
                  "general_ledger_accounts.empty.title",
                )}
              >
                {getLocalizedText("general_ledger_accounts.empty.content")}
              </EmptyState>
            }
          />
        </>
      )}
    </>
  );

  if (asCard) {
    return <Card>{content}</Card>;
  }

  return content;
};
